import { CC_DELETE_CONFIRMATION } from 'constant';
import { ASSETS_PATH } from 'constant/paymentConstants';
import {
  paymentDetailsMap,
  paymentHiddenDigitsMap,
  paymentIconMap,
  paymentLastDigitsMap,
  paymentSubmitLabelMap,
} from 'maps/paymentMaps/paymentMaps';

/**
 * Returns the masked or hidden digits for a given payment method.
 *
 * Retrieves the relevant digits using getDigitsForPayment and applies the appropriate
 * masking function from paymentHiddenDigitsMap based on the payment method type.
 * Returns an empty string if not applicable.
 *
 * @param paymentMethod The payment method object.
 * @returns {string} The masked or hidden digits string, or an empty string if not applicable.
 */
const getHiddenDigitsForPayment = paymentMethod => {
  const digits = getDigitsForPayment(paymentMethod);
  return (
    paymentHiddenDigitsMap[paymentMethod?.paymentMethodType]?.(digits) ?? ''
  );
};

/**
 * Generates the confirmation text for the delete dialog of a payment method.
 *
 * Combines the CC_DELETE_CONFIRMATION message with the relevant digits for the given payment method.
 *
 * @param paymentMethod The payment method object to extract digits from.
 * @returns {string} The formatted delete confirmation text.
 */
const getDeleteDialogText = paymentMethod =>
  `${CC_DELETE_CONFIRMATION} ${getDigitsForPayment(paymentMethod)}?`;

/**
 * Returns the icon asset path for a given payment method.
 *
 * Looks up the payment method type in paymentIconMap and invokes the corresponding function.
 * If no icon is found, returns the path to the common icon asset.
 *
 * @param paymentMethod The payment method object.
 * @returns {string} The icon asset path for the payment method.
 */
const getPaymentIcon = paymentMethod =>
  paymentIconMap[paymentMethod?.paymentMethodType]?.(paymentMethod) ??
  getAssetsPath('COMMON_ICON');

/**
 * Returns the formatted payment details for a given payment method.
 *
 * Looks up the payment method type in paymentDetailsMap and invokes the corresponding function.
 * Returns an empty string if no details are available.
 *
 * @param paymentMethod The payment method object.
 * @returns {string} The formatted payment details string, or an empty string if not applicable.
 */
const getPaymentDetails = paymentMethod =>
  paymentDetailsMap[paymentMethod?.paymentMethodType]?.(paymentMethod) ?? '';

/**
 * Returns the asset path for a given icon name by replacing the '%s' placeholder in ASSETS_PATH.
 *
 * @param {string} iconName The name of the icon to insert into the asset path.
 * @returns {string} The full asset path with the icon name included.
 */
const getAssetsPath = iconName => ASSETS_PATH.replace('%s', iconName);

/**
 * Gets the formatted submit button label based on payment type.
 *
 * @param {number} amount - The fixed payment amount for subscription or usage
 * @param {string} payment - The payment transaction type (e.g., subscription, usage, other)
 * @param {number} other - Other amount to pay
 * @returns {string} Formatted button label or empty string if invalid
 */
const getSubmitButtonLabel = ({ amount, payment, other }) =>
  paymentSubmitLabelMap[payment]?.({ amount, other }) ?? '';

const getDigitsForPayment = paymentMethod =>
  paymentLastDigitsMap[paymentMethod?.paymentMethodType]?.(paymentMethod) ?? '';

export {
  getAssetsPath,
  getDeleteDialogText,
  getHiddenDigitsForPayment,
  getPaymentDetails,
  getPaymentIcon,
  getSubmitButtonLabel,
};
